import React, { Component } from 'react';
import { graphql } from 'react-apollo';
import gql from 'graphql-tag';
import FoundationLayout from '../components/layout/FoundationLayout';
import FoundationMultiSelect from '../components/common/FoundationMultiSelect';
import { getFoundationStudent } from '../foundationSession';
import './_foundationCreateExam.scss';

/**
 * IIT Foundation — Create Exam builder.
 *
 * The student builds a custom exam by choosing any number of Subjects and Topics
 * (both multi-select), drawn from the class question bank (getFoundationExamTopics
 * → questions-table subject/chapter). Starting the exam runs the same full-screen
 * runner in `custom` mode.
 */
const GET_FOUNDATION_EXAM_TOPICS = gql`
  query ($class_id: Int!) {
    getFoundationExamTopics(class_id: $class_id) {
      subject_id
      subject
      topic
      question_count
    }
  }
`;

// colours follow the foundation theme's subject palette (theme/README.md)
const SUBJECT_STYLE = [
  { test: /phys/i, color: 'var(--fdn-violet)', icon: 'fa-atom' },
  { test: /chem/i, color: 'var(--fdn-teal)', icon: 'fa-flask' },
  { test: /bio/i, color: 'var(--fdn-green)', icon: 'fa-dna' },
  { test: /math/i, color: 'var(--fdn-orange)', icon: 'fa-square-root-alt' },
  { test: /reason|apti|mental/i, color: 'var(--fdn-yellow)', icon: 'fa-brain' },
  { test: /sci/i, color: 'var(--fdn-pink)', icon: 'fa-microscope' },
  { test: /eng|lang/i, color: 'var(--fdn-blue)', icon: 'fa-book-open' },
];
const styleFor = (subject) => SUBJECT_STYLE.find((x) => x.test.test(subject || '')) || { color: 'var(--fdn-muted)', icon: 'fa-layer-group' };

class CreateExamView extends Component {
  state = { subjects: [], topics: [], search: '' }; // selected subject_ids / topic strings

  get allTopics() {
    return this.props.exams && this.props.exams.getFoundationExamTopics
      ? this.props.exams.getFoundationExamTopics
      : [];
  }

  get subjectOptions() {
    const byId = {};
    const list = [];
    this.allTopics.forEach((t) => {
      if (t.subject_id == null) return;
      const id = Number(t.subject_id);
      if (byId[id] === undefined) {
        byId[id] = list.length;
        list.push({ id, name: t.subject || 'Other', questions: 0 });
      }
      list[byId[id]].questions += Number(t.question_count) || 0;
    });
    return list;
  }

  // topic options for the dropdown: filtered by picked subjects (none = all),
  // deduped by topic name (a topic can appear under one subject), counts summed.
  get topicOptions() {
    const { subjects } = this.state;
    const byTopic = {};
    const list = [];
    this.allTopics.forEach((t) => {
      if (subjects.length && subjects.indexOf(Number(t.subject_id)) === -1) return;
      if (!t.topic) return;
      if (byTopic[t.topic] === undefined) {
        byTopic[t.topic] = list.length;
        list.push({ topic: t.topic, subject: t.subject, count: 0 });
      }
      list[byTopic[t.topic]].count += Number(t.question_count) || 0;
    });
    return list;
  }

  onSubjectsChange = (next) => {
    this.setState((s) => {
      const patch = { subjects: next };
      // dropping a subject also drops its now-hidden topics
      const allowed = next.length
        ? this.allTopics.filter((t) => next.indexOf(Number(t.subject_id)) !== -1).map((t) => t.topic)
        : null;
      if (allowed) patch.topics = s.topics.filter((tp) => allowed.indexOf(tp) !== -1);
      return patch;
    });
  };
  onTopicsChange = (next) => this.setState({ topics: next });

  start = () => {
    const { subjects, topics } = this.state;
    const qs = new URLSearchParams({ mode: 'custom' });
    if (subjects.length) qs.set('subjects', subjects.join(','));
    if (topics.length) qs.set('topics', JSON.stringify(topics));
    const url = `/iit-foundation/take-test?${qs.toString()}`;
    if (this.props.history) this.props.history.push(url);
    else window.location.assign(url);
  };

  render() {
    const student = getFoundationStudent() || {};
    const loading = this.props.exams && this.props.exams.loading;
    const subjectOptions = this.subjectOptions;
    const topicOptions = this.topicOptions;
    const { subjects: selSub, topics: selTop } = this.state;
    const canStart = selSub.length > 0 || selTop.length > 0;

    return (
      <FoundationLayout student={getFoundationStudent()} title="Create Exam">
        <div className="fce">
          <div className="fce-head fdn-hero fdn-hero--orange">
            <div className="fdn-hero__row">
              <span className="fdn-hero__icon"><i className="fas fa-magic" /></span>
              <div>
                <h2 className="fdn-hero__title">Create Exam</h2>
                <p className="fdn-hero__sub">
                  {student.class ? `${student.class} · ` : ''}
                  Build your own exam — choose any subjects and topics. Leave a section empty to include all of it.
                </p>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="fce-empty fce-loading fdn-card">
              <span className="fdn-skel" style={{ width: '30%' }} />
              <div className="fce-selects">
                <span className="fdn-skel fce-skel-field" />
                <span className="fdn-skel fce-skel-field" />
              </div>
              <p><i className="fas fa-circle-notch fa-spin" /> Loading the question bank…</p>
            </div>
          ) : !this.allTopics.length ? (
            <div className="fce-empty fdn-card fdn-empty">
              <div className="fdn-empty__icon"><i className="fas fa-box-open" /></div>
              <h4>Nothing to mix yet 🧪</h4>
              <p>No question topics available for your class yet.</p>
            </div>
          ) : (
            <>
              <section className="fce-section fdn-card fdn-pop">
                <div className="fdn-card__head">
                  <span className="fdn-card__icon fce-section-icon"><i className="fas fa-sliders-h" /></span>
                  <div>
                    <h3 className="fdn-card__title">Pick your mix 🎯</h3>
                    <p className="fdn-card__sub">Choose subjects first, then narrow down by topic.</p>
                  </div>
                </div>
                <div className="fce-selects">
                  <FoundationMultiSelect
                    label="Subjects"
                    placeholder="All subjects"
                    noun="subjects"
                    options={subjectOptions.map((s) => ({ value: s.id, label: s.name, count: s.questions, color: styleFor(s.name).color }))}
                    selected={selSub}
                    onChange={this.onSubjectsChange}
                  />
                  <FoundationMultiSelect
                    label="Topics"
                    placeholder="All topics"
                    noun="topics"
                    searchable
                    options={topicOptions.map((t) => ({ value: t.topic, label: t.topic, count: t.count, color: styleFor(t.subject).color }))}
                    selected={selTop}
                    onChange={this.onTopicsChange}
                  />
                </div>
              </section>

              {/* Action bar --------------------------------------------- */}
              <div className="fce-bar fdn-card">
                <div className="fce-bar-sum">
                  <span className="fce-bar-pill"><i className="fas fa-book" /> <b>{selSub.length || subjectOptions.length}</b> subject{(selSub.length || subjectOptions.length) === 1 ? '' : 's'}</span>
                  <span className="fce-bar-dot">·</span>
                  <span className="fce-bar-pill"><i className="fas fa-tags" /> <b>{selTop.length || topicOptions.length}</b> topic{(selTop.length || topicOptions.length) === 1 ? '' : 's'}</span>
                </div>
                <button type="button" className="fce-start fdn-btn fdn-btn--primary fdn-btn--lg" disabled={!canStart} onClick={this.start}>
                  Start Exam <i className="fas fa-arrow-right" />
                </button>
              </div>
            </>
          )}
        </div>
      </FoundationLayout>
    );
  }
}

const CreateExam = graphql(GET_FOUNDATION_EXAM_TOPICS, {
  name: 'exams',
  skip: () => { const s = getFoundationStudent(); return !(s && s.grade_id); },
  options: () => {
    const s = getFoundationStudent();
    return { variables: { class_id: Number(s.grade_id) }, fetchPolicy: 'cache-and-network' };
  },
})(CreateExamView);

/** Renders a prompt when there's no session; otherwise the create-exam builder. */
class IITFoundationCreateExam extends Component {
  render() {
    const s = getFoundationStudent();
    if (!s || !s.grade_id) {
      return (
        <FoundationLayout student={s}>
          <div className="fce">
            <div className="fce-empty fdn-card fdn-empty">
              <div className="fdn-empty__icon"><i className="fas fa-user-lock" /></div>
              <h4>Hold on! 🔐</h4>
              <p>Please log in to create an exam.</p>
            </div>
          </div>
        </FoundationLayout>
      );
    }
    return <CreateExam {...this.props} />;
  }
}

export default IITFoundationCreateExam;
